import { UseMutateFunction, useQueryClient } from '@tanstack/react-query'
import { PaginationState, createColumnHelper } from '@tanstack/react-table'
import { theme } from 'Providers/ThemeProvider'
import { NA } from 'constans/common'
import { PRODUCTS } from 'constans/queryKeys'
import useToastify from 'hooks/useToastify'
import { useCallback, useMemo, useState } from 'react'
import { Button, Table, Text } from 'shared/components'
import { ApiResponseModel } from 'types/common'
import { AllowChangeTemProductModel, ProductModel } from 'types/products'
import { formatNumber } from 'utils/formatNumber'

interface ProductListProps {
  data?: Array<ProductModel>
  pageSize: number
  pageIndex: number
  onPageChange: (state: PaginationState) => void
  isPending?: boolean
  mutate: UseMutateFunction<
    ApiResponseModel<any>,
    Error,
    AllowChangeTemProductModel,
    unknown
  >
}

const ProductList = ({
  data = [],
  pageSize,
  isPending = false,
  pageIndex,
  mutate,
  onPageChange,
}: ProductListProps) => {
  const [currentProductCode, setCurrentProductCode] = useState('')
  const columnHelper = createColumnHelper<ProductModel>()
  const queryClient = useQueryClient()
  const { showSuccess, showError } = useToastify()

  const handleActiveChangeTem = useCallback(
    (productCode: string) => {
      mutate(
        {
          lstProductUpdate: [
            {
              allowChangeTem: 1,
              productCode,
            },
          ],
        },
        {
          onSuccess: d => {
            if (d.ErrorCode === 1) {
              showError('Đã xảy ra lỗi,vui lòng thử lại sau!')
              return
            }
            queryClient.invalidateQueries({
              queryKey: [PRODUCTS],
            })
            showSuccess(
              'Cập nhật thành công, vui lòng xem lại danh sách sản phẩm!'
            )
          },
        }
      )
    },
    [queryClient, mutate, showError, showSuccess]
  )

  const columns = useMemo(
    () => [
      columnHelper.accessor('ProductCode', {
        header: 'Mã sản phẩm',
        cell: val => val.getValue(),
      }),
      columnHelper.accessor('ProductName', {
        header: 'Tên sản phẩm',
        cell: val => val.getValue(),
      }),
      columnHelper.accessor('Price', {
        header: 'Giá',
        cell: val => `${formatNumber(val.getValue())}đ`,
      }),
      columnHelper.accessor('AllowChangeTem', {
        header: 'Cho phép tích tem',
        cell: val => {
          const isAllow = val.getValue() === 1
          return (
            <Text color={theme.palette[isAllow ? 'success' : 'error'].main}>
              {isAllow ? 'Có' : 'Không'}
            </Text>
          )
        },
      }),
      columnHelper.accessor('ProductId', {
        header: '',
        cell: val => {
          const { AllowChangeTem, ProductCode } = val.row.original
          return AllowChangeTem === 0 ? (
            <Button
              sx={{ minWidth: 180 }}
              onClick={() => {
                setCurrentProductCode(ProductCode)
                handleActiveChangeTem(ProductCode)
              }}
              disabled={isPending}
              loading={isPending && currentProductCode === ProductCode}
              color="success"
            >
              Cho phép tích tem
            </Button>
          ) : (
            <>{NA}</>
          )
        },
      }),
    ],
    [columnHelper, handleActiveChangeTem, isPending, currentProductCode]
  )

  const totalRows = useMemo(() => {
    return data && data.length ? data[0]?.TotalRow || 0 : 0
  }, [data])

  return (
    <Table
      getRowId={original => original.ProductId}
      data={data}
      columns={columns}
      pageIndex={pageIndex}
      pageSize={pageSize}
      totalRows={totalRows}
      onPageChange={onPageChange}
      isShowPagination
    />
  )
}

export default ProductList
